import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { CzechSuklRsAtcGroupResponse } from 'com.cogvio.pricemonitor.api';
import { GROUP_TYPE } from '~/Api/User/ProductGroup';
import { handleError } from '~/ErrorHandler';
import { flashSuccessMessage, flashWarningMessage } from '~/FlashMessage';
import { createGroup, redirectToGroup } from '~/ProductGroup';
import type { ThunkDispatch } from '~/Store';

import { FiltersValue } from '../components/Filters';
import { buildSuklCodesFromGroups } from '../utils/buildSuklCodesFromGroups';
import {
	buildFolderNameFromFilters,
	buildFolderNameFromFiltersAsync,
	resolveFolderNameFromLoadedGroups,
} from '../utils/buildFolderNameFromFilters';
import { fetchAllFilteredAvailabilityGroups } from '../utils/fetchAllFilteredAvailabilityGroups';

export function useAvailabilityCreateFolder(filters: FiltersValue, loadedGroups?: CzechSuklRsAtcGroupResponse[]) {
	const dispatch: ThunkDispatch<AnyAction> = useDispatch();
	const [isCreating, setIsCreating] = useState(false);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [folderName, setFolderName] = useState(
		() => resolveFolderNameFromLoadedGroups(filters, loadedGroups) ?? buildFolderNameFromFilters(filters)
	);
	const [isResolvingFolderName, setIsResolvingFolderName] = useState(false);

	const hasActiveFilter = filters.items.length > 0 || filters.rs.length > 0;

	useEffect(() => {
		let cancelled = false;
		const resolvedName = resolveFolderNameFromLoadedGroups(filters, loadedGroups);

		if (resolvedName !== null) {
			setFolderName(resolvedName);
			setIsResolvingFolderName(false);
			return undefined;
		}

		if (filters.items.length === 0) {
			setFolderName(buildFolderNameFromFilters(filters));
			setIsResolvingFolderName(false);
			return undefined;
		}

		setIsResolvingFolderName(true);

		buildFolderNameFromFiltersAsync(filters).then((nextName) => {
			if (cancelled) {
				return;
			}

			setFolderName(nextName);
			setIsResolvingFolderName(false);
		});

		return () => {
			cancelled = true;
		};
	}, [filters, loadedGroups]);

	const openCreateFolderDialog = () => {
		if (hasActiveFilter && !isCreating && !isResolvingFolderName) {
			setIsDialogOpen(true);
		}
	};

	const closeCreateFolderDialog = () => {
		if (!isCreating) {
			setIsDialogOpen(false);
		}
	};

	const handleCreateFolder = async (name: string) => {
		if (!hasActiveFilter || isCreating) {
			return;
		}

		setIsCreating(true);

		try {
			const groups = await fetchAllFilteredAvailabilityGroups(filters);
			const lists = buildSuklCodesFromGroups(groups);

			if (lists.length === 0) {
				dispatch(flashWarningMessage('Ve filtrovaných výsledcích nejsou žádné SÚKL kódy pro vytvoření složky.'));
				return;
			}

			const { productGroup } = await dispatch(createGroup(name, GROUP_TYPE, null, null, null, lists));

			setIsDialogOpen(false);
			dispatch(flashSuccessMessage(`Složka ${name} byla vytvořena.`));
			dispatch(redirectToGroup(productGroup));
		} catch (error: unknown) {
			dispatch(handleError(error));
		} finally {
			setIsCreating(false);
		}
	};

	return {
		isDialogOpen,
		openCreateFolderDialog,
		closeCreateFolderDialog,
		handleCreateFolder,
		isCreating,
		hasActiveFilter,
		folderName,
		isResolvingFolderName,
	};
}
